/**
 * TermsPage  — /terms
 *
 * MomentSent LLC Terms of Service.
 * Effective date: May 15, 2026 (LLC formation date).
 * Governing law: State of North Carolina.
 */

import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';

export default function TermsPage() {
  return (
    <LegalShell title="Terms of Service" effectiveDate="May 15, 2026">
      <PageSEO
        title="Terms of Service | MomentSent"
        description="Read the MomentSent terms of service to understand your rights and responsibilities as a subscriber."
        canonical="https://momentsent.com/terms"
      />

      <Section title="1. Acceptance of Terms">
        <p>
          By creating an account or using the MomentSent Service, you agree to be bound by
          these Terms of Service ("Terms") and our{' '}
          <Link to="/privacy" className="text-sage hover:underline">Privacy Policy</Link>,
          which is incorporated by reference. If you do not agree to these Terms, do not use
          the Service.
        </p>
        <p>
          These Terms constitute a legally binding agreement between you and MomentSent LLC,
          a North Carolina limited liability company ("MomentSent", "we", "us", or "our"),
          with offices in Hubert, NC.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <p>
          You must be at least 18 years old to use the Service. By using the Service, you
          represent and warrant that you are 18 or older and have the legal capacity to enter
          into a binding contract. If you are using the Service on behalf of an organization,
          you represent that you have the authority to bind that organization to these Terms.
        </p>
      </Section>

      <Section title="3. Description of Service">
        <p>
          MomentSent is a subscription-based platform that automates the scheduling and mailing
          of physical printed cards on your behalf. You create a list of contacts and occasions
          and provide the card messages; MomentSent provides card templates, prints the cards
          through our fulfillment partners, and mails them via postal services.
        </p>
        <p>
          Features available to subscribers include: automated occasion reminders, physical card
          printing and mailing, on-demand card sending (Premium plan only, see Section 5.8),
          Relationship Wrapped year-in-review, recipient delivery
          portals, and a referral program. Feature availability varies by subscription tier;
          current tier features are described at{' '}
          <Link to="/pricing" className="text-sage hover:underline">momentsent.com/pricing</Link>.
        </p>
        <p>
          Every card MomentSent sends is a folded greeting card mailed in a sealed envelope, with
          printing and first-class postage included. Each plan includes a set number of cards per
          year; cards sent beyond that yearly allowance are billed at a flat per-card rate, as
          described in Section 5.7.
        </p>
      </Section>

      <Section title="4. Accounts">
        <SubSection title="4.1 Registration">
          <p>
            You must provide accurate and complete information when creating your account. You
            agree to keep your contact information up to date. You are responsible for maintaining
            the confidentiality of your password and for all activity that occurs under your account.
          </p>
        </SubSection>

        <SubSection title="4.2 One account per person">
          <p>
            Each individual may maintain only one active MomentSent account. Duplicate accounts
            created to circumvent subscription limits, referral rewards, or trial periods are
            prohibited and may be terminated without refund.
          </p>
        </SubSection>

        <SubSection title="4.3 Account security">
          <p>
            You agree to notify us immediately at{' '}
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>{' '}
            if you suspect unauthorized use of your account. MomentSent is not liable for any
            loss resulting from unauthorized use of your account.
          </p>
        </SubSection>
      </Section>

      <Section title="5. Subscriptions and Payment">
        <SubSection title="5.1 Free trial">
          <p>
            New accounts receive a 14-day free trial. No credit card is required to begin the
            trial. At the end of the trial, your account will be downgraded and card sending will
            pause unless you subscribe to a paid plan.
          </p>
        </SubSection>

        <SubSection title="5.2 Subscription plans">
          <p>
            Paid plans are billed monthly or annually as selected at checkout. All prices are in
            US dollars and are subject to applicable taxes. Current pricing is available at{' '}
            <Link to="/pricing" className="text-sage hover:underline">momentsent.com/pricing</Link>.
            We reserve the right to change pricing with 30 days' notice.
          </p>
        </SubSection>

        <SubSection title="5.3 Automatic renewal">
          <p>
            Subscriptions automatically renew at the end of each billing period. By subscribing,
            you authorize MomentSent (via Stripe) to charge your payment method on file at the
            then-current renewal price. You may cancel auto-renewal at any time; cancellation
            takes effect at the end of the current billing period.
          </p>
        </SubSection>

        <SubSection title="5.4 Failed payments">
          <p>
            If a payment fails, we will attempt to collect it up to three times over seven days.
            If payment cannot be collected, your account will be suspended and card sending will
            pause until payment is resolved. You remain responsible for any outstanding balance.
          </p>
        </SubSection>

        <SubSection title="5.5 Refunds">
          <p>
            Because our service triggers physical card printing and postage, we generally cannot
            offer refunds once a card has been submitted to a fulfillment partner. However, if
            a card fails to deliver due to a MomentSent error (e.g., a vendor-side printing
            defect), we will reprint and reship or issue a credit at our discretion. Refund
            requests must be submitted within 30 days of the scheduled send date to{' '}
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>.
          </p>
        </SubSection>

        <SubSection title="5.6 Referral credits">
          <p>
            When a person you referred makes their first successful subscription payment,
            a $7.99 Stripe balance credit is applied to your account and automatically
            deducted from your next invoice. Credits are issued to the referring account only.
            Credits have no cash value, are non-transferable, and expire if your account is closed.
          </p>
        </SubSection>

        <SubSection title="5.7 Included cards and overage">
          <p>
            Each subscription plan includes a yearly allowance of folded cards, with printing and
            first-class postage included: <strong>12 cards/year</strong> on the Thoughtful plan,{' '}
            <strong>24 cards/year</strong> on the Connected plan, and <strong>48 cards/year</strong>{' '}
            on the Premium plan. Annual subscribers receive their full yearly allowance at the start
            of each subscription year. Monthly subscribers accrue their allowance over the year, and
            unused cards carry forward up to the plan's annual amount.
          </p>
          <p>
            If you send more cards than your plan's yearly allowance, each additional card is billed
            at a flat <strong>$5.99</strong>. Overage charges are added to your next Stripe invoice.
            International surcharges, where applicable, may also apply. The general refund policy in
            Section 5.5 applies to overage charges in the event of a vendor-side production error.
          </p>
        </SubSection>

        <SubSection title="5.8 On-demand card sending (Send Now, Premium only)">
          <p>
            Premium plan subscribers may send a card to any of their contacts at any time using
            the Send Now feature, without creating a scheduled occasion. The following terms
            apply to all Send Now sends:
          </p>
          <ul>
            <li>The card is dispatched to the fulfillment vendor immediately upon your confirmation. No editing or cancellation is possible after you click "Send card."</li>
            <li>Send Now cards count toward your plan's yearly card allowance. If you are over your allowance, the card is billed at the flat <strong>$5.99</strong> overage rate described in Section 5.7.</li>
            <li>International surcharges apply to Send Now cards in the same manner as scheduled cards.</li>
            <li>Because Send Now cards are dispatched immediately, refunds are not available except in the event of a vendor-side production or printing error, in which case the refund policy in Section 5.5 applies.</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="6. Your Responsibilities">
        <SubSection title="6.1 Accurate recipient information">
          <p>
            You are solely responsible for the accuracy of recipient names and mailing addresses
            you enter. MomentSent is not liable for cards that fail to deliver due to incorrect,
            incomplete, or outdated addresses you provide. We offer address verification as a
            convenience; verification does not guarantee deliverability.
          </p>
        </SubSection>

        <SubSection title="6.2 Appropriate card content">
          <p>
            By submitting card messages, photos, or other content through
            the Service, you represent that:
          </p>
          <ul>
            <li>You own or have the right to use the content (including any photos).</li>
            <li>The content does not infringe any third-party intellectual property rights.</li>
            <li>The content is not defamatory, obscene, threatening, discriminatory, or otherwise unlawful.</li>
            <li>You have the recipient's consent where required by applicable law.</li>
          </ul>
          <p>
            MomentSent reserves the right to refuse to print or mail any card whose content
            we determine, at our sole discretion, violates these Terms or applicable law.
          </p>
        </SubSection>

        <SubSection title="6.3 Recipient relationships and minor recipients">
          <p>
            By adding a recipient to your account, you represent that you have a genuine personal
            relationship with that individual and that mailing a physical card to them is
            consistent with that relationship. You may add the mailing address of a minor (for
            example, a child's birthday) provided that you are the parent, legal guardian, or
            otherwise have the authority to send mail to that individual on behalf of the
            relationship you represent.
          </p>
          <p>
            Recipient addresses are provided by you, an adult account holder, and are not
            collected directly from recipients. MomentSent does not knowingly solicit, receive,
            or process personal information from children under 13 acting as users of the Service.
          </p>
        </SubSection>

        <SubSection title="6.4 Recipient opt-out and do-not-mail requests">
          <p>
            If a recipient contacts MomentSent requesting that no further mail be sent to their
            address, we will notify you by email and disable future mailings to that contact in
            your account. You are responsible for maintaining an appropriate relationship with
            each recipient and for promptly honoring any request to stop receiving cards.
          </p>
          <p>
            MomentSent is not liable for any claim brought by a recipient arising from your use
            of the Service to send cards to that recipient, including claims under anti-harassment,
            privacy, or postal laws. Your indemnification obligations under Section 12 apply
            fully to such claims.
          </p>
        </SubSection>

        <SubSection title="6.5 Permitted use">
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Send unsolicited communications (spam) to individuals you do not have a genuine personal relationship with.</li>
            <li>Harass, stalk, or threaten any person.</li>
            <li>Impersonate any individual or entity.</li>
            <li>Circumvent any security feature or access controls.</li>
            <li>Resell or commercially exploit the Service without our written consent.</li>
            <li>Use automated tools (bots, scrapers) to access the Service.</li>
            <li>Violate any applicable local, state, national, or international law or regulation.</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="7. Physical Mail Limitations">
        <p>
          Physical mail delivery is subject to the policies, delays, and failures of postal
          services and our fulfillment vendor (Thankster) and is outside
          MomentSent's direct control. We cannot guarantee delivery dates, and delivery timelines
          are estimates only.
        </p>
        <p>
          International cards may be subject to customs delays, import duties, or restrictions
          in the destination country. MomentSent is not responsible for cards held, damaged,
          or returned by customs.
        </p>
        <p>
          If the postal service returns a card as undeliverable, we will notify you by email.
          We will not automatically reship unless you correct the address and request a resend.
          Reshipping fees may apply.
        </p>
        <p>
          <strong>Force majeure:</strong> MomentSent is not liable for any failure or delay in
          card production or delivery caused by circumstances beyond our reasonable control,
          including but not limited to postal service disruptions or strikes, carrier outages,
          fulfillment vendor failures, natural disasters, pandemics, acts of government or
          regulatory authority, war, or other force majeure events. In such circumstances we
          will make reasonable efforts to notify affected users and to resume normal service
          as quickly as practicable, but we make no guarantee of a specific timeline.
        </p>
      </Section>

      <Section title="8. Intellectual Property">
        <SubSection title="8.1 MomentSent IP">
          <p>
            The Service, including its design, software, card templates, and all related
            intellectual property, is owned by MomentSent LLC or its licensors. Nothing in these
            Terms grants you any right, title, or interest in the Service beyond the limited right
            to use it as described herein.
          </p>
        </SubSection>

        <SubSection title="8.2 Your content">
          <p>
            You retain ownership of all content you upload to the Service (messages, photos).
            By submitting content, you grant MomentSent a limited, non-exclusive,
            royalty-free license to use, reproduce, and transmit that content solely as necessary
            to provide the Service (e.g., to print and mail your card). We will never use your
            content for advertising or share it with third parties except as required to fulfill
            your card orders.
          </p>
        </SubSection>
      </Section>

      <Section title="9. Third-Party Services">
        <p>
          The Service integrates with third-party providers including Stripe, Thankster,
          Twilio, SendGrid, and Supabase. Your use of those integrations is
          subject to those providers' own terms of service and privacy policies. MomentSent
          is not responsible for the acts or omissions of any third-party service provider.
        </p>
      </Section>

      <Section title="10. Disclaimer of Warranties">
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
          EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
          THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL
          COMPONENTS. WE DO NOT WARRANT THE ACCURACY OR COMPLETENESS OF ANY CONTENT
          GENERATED BY THE SERVICE.
        </p>
      </Section>

      <Section title="11. Limitation of Liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, MOMENTSENT LLC AND ITS OFFICERS,
          DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA,
          GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF
          (OR INABILITY TO USE) THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY
          OF SUCH DAMAGES.
        </p>
        <p>
          IN NO EVENT SHALL MOMENTSENT'S TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS
          ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE EXCEED THE GREATER OF
          (A) $50 USD OR (B) THE TOTAL FEES YOU PAID TO MOMENTSENT IN THE 12 MONTHS
          PRECEDING THE CLAIM.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion or limitation of certain damages.
          In such jurisdictions, MomentSent's liability shall be limited to the fullest
          extent permitted by law.
        </p>
      </Section>

      <Section title="12. Indemnification">
        <p>
          You agree to indemnify, defend, and hold harmless MomentSent LLC and its officers,
          directors, employees, agents, and successors from and against any claims, liabilities,
          damages, judgments, awards, losses, costs, and expenses (including reasonable attorneys'
          fees) arising out of or relating to: (a) your use of the Service; (b) your violation of
          these Terms; (c) content you submit to the Service; or (d) your violation of any rights
          of a third party.
        </p>
      </Section>

      <Section title="13. Termination">
        <SubSection title="13.1 By you">
          <p>
            You may close your account at any time by contacting{' '}
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>{' '}
            or through account settings. Closing your account cancels your subscription at the
            end of the current billing period. We do not pro-rate partial months.
          </p>
        </SubSection>

        <SubSection title="13.2 By MomentSent">
          <p>
            We may suspend or terminate your account immediately, without notice, if we determine
            you have violated these Terms, including but not limited to sending prohibited content,
            creating duplicate accounts to abuse trials or referrals, or engaging in fraudulent
            payment activity. We reserve the right to refuse service to anyone for any reason not
            prohibited by law.
          </p>
        </SubSection>

        <SubSection title="13.3 Effect of termination">
          <p>
            Upon termination, your right to use the Service ceases immediately. Sections 8, 10,
            11, 12, 14, and 15 survive termination. Cards already submitted to fulfillment
            partners at the time of termination may still be sent.
          </p>
        </SubSection>
      </Section>

      <Section title="14. Dispute Resolution and Binding Arbitration">
        <p>
          PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR
          RIGHT TO FILE A LAWSUIT IN COURT.
        </p>

        <SubSection title="14.1 Informal resolution (required first step)">
          <p>
            Before initiating any formal dispute process, you agree to contact MomentSent at{' '}
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>{' '}
            with a written description of the dispute, the relief sought, and your contact
            information. Both parties agree to negotiate in good faith for at least 30 days.
            This informal resolution requirement is a condition precedent to arbitration or
            any other legal proceeding.
          </p>
        </SubSection>

        <SubSection title="14.2 Binding arbitration">
          <p>
            If the dispute is not resolved through informal negotiation, you and MomentSent
            agree to resolve any dispute, claim, or controversy arising out of or relating to
            these Terms or the Service, including questions about the existence, validity,
            interpretation, breach, or termination of these Terms, through final and binding
            individual arbitration, rather than in court.
          </p>
          <p>
            Arbitration shall be administered by the American Arbitration Association ("AAA")
            under its Consumer Arbitration Rules, which are available at{' '}
            <strong>adr.org</strong>. If the AAA is unavailable or unwilling to administer the
            arbitration, the parties shall agree on an alternative arbitration provider. The
            arbitration shall be conducted in English, and the arbitrator's award shall be final
            and binding and may be entered as a judgment in any court of competent jurisdiction.
          </p>
          <p>
            For claims under $10,000 USD, arbitration may be conducted by telephone, video
            conference, or written submissions at the election of the party bringing the claim,
            unless the arbitrator finds that an in-person hearing is necessary. For claims over
            $10,000, the location of any in-person hearing shall be Onslow County, North Carolina,
            or such other location mutually agreed upon by the parties.
          </p>
          <p>
            The arbitrator has exclusive authority to resolve any dispute relating to the
            interpretation, applicability, enforceability, or formation of this agreement to
            arbitrate, including whether it is void or voidable, except that a court shall
            decide any challenge to the class action waiver in Section 15.4.
          </p>
        </SubSection>

        <SubSection title="14.3 Arbitration fees">
          <p>
            Payment of all filing, administration, and arbitrator fees will be governed by the
            AAA Consumer Arbitration Rules. MomentSent will pay arbitration costs for claims
            that do not exceed $10,000, provided the claim is not frivolous. For claims exceeding
            $10,000, arbitration costs will be shared equally unless the arbitrator determines
            otherwise based on applicable rules.
          </p>
        </SubSection>

        <SubSection title="14.4 Class action and jury trial waiver">
          <p>
            YOU AND MOMENTSENT AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR
            OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED
            CLASS, COLLECTIVE, CONSOLIDATED, PRIVATE ATTORNEY GENERAL, OR REPRESENTATIVE
            PROCEEDING. FURTHER, UNLESS BOTH PARTIES AGREE OTHERWISE, THE ARBITRATOR MAY NOT
            CONSOLIDATE MORE THAN ONE PERSON'S CLAIMS AND MAY NOT OTHERWISE PRESIDE OVER ANY
            FORM OF REPRESENTATIVE PROCEEDING.
          </p>
          <p>
            IF THIS CLASS ACTION WAIVER IS FOUND UNENFORCEABLE AS TO A PARTICULAR CLAIM, THEN
            THAT CLAIM SHALL BE SEVERED FROM THE ARBITRATION AND PROCEED IN COURT, SUBJECT TO
            YOUR AND MOMENTSENT'S RIGHT TO APPEAL THE FINDING OF UNENFORCEABILITY. ALL OTHER
            CLAIMS SHALL REMAIN IN ARBITRATION.
          </p>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, YOU WAIVE YOUR RIGHT TO A JURY
            TRIAL FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE.
          </p>
        </SubSection>

        <SubSection title="14.5 Exceptions to arbitration">
          <p>
            Notwithstanding the foregoing, either party may bring an individual action in small
            claims court for disputes within that court's jurisdiction. Either party may also seek
            emergency injunctive or other equitable relief from a court of competent jurisdiction
            to prevent actual or threatened infringement, misappropriation, or violation of
            intellectual property rights or confidential information, without first engaging in
            arbitration or the informal dispute-resolution process.
          </p>
        </SubSection>

        <SubSection title="14.6 Opt-out right">
          <p>
            You have the right to opt out of this arbitration agreement by sending written notice
            to{' '}
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>{' '}
            within 30 days of first accepting these Terms. Your notice must include your name,
            email address, and a clear statement that you wish to opt out of arbitration. Opting
            out does not affect any other provision of these Terms.
          </p>
        </SubSection>

        <SubSection title="14.7 Governing law">
          <p>
            These Terms and any disputes not subject to arbitration are governed by the laws of
            the State of North Carolina, without regard to its conflict-of-law provisions. Any
            legal action that proceeds to court (including small claims and injunctive relief)
            shall be brought exclusively in the state or federal courts located in Onslow County,
            North Carolina, and you consent to personal jurisdiction in those courts.
          </p>
        </SubSection>
      </Section>

      <Section title="15. General Provisions">
        <SubSection title="15.1 Entire agreement">
          <p>
            These Terms, together with our Privacy Policy and any additional terms you agree to
            when using specific features, constitute the entire agreement between you and MomentSent
            regarding the Service.
          </p>
        </SubSection>

        <SubSection title="15.2 Modifications">
          <p>
            We may update these Terms from time to time. Material changes will be communicated
            by email or in-app notice at least 14 days before taking effect. Continued use of
            the Service after the effective date constitutes acceptance.
          </p>
        </SubSection>

        <SubSection title="15.3 Severability">
          <p>
            If any provision of these Terms is found to be unenforceable, that provision shall
            be modified to the minimum extent necessary to make it enforceable, and the remainder
            of the Terms shall remain in full force.
          </p>
        </SubSection>

        <SubSection title="15.4 No waiver">
          <p>
            MomentSent's failure to enforce any right or provision of these Terms shall not
            constitute a waiver of that right or provision.
          </p>
        </SubSection>

        <SubSection title="15.5 Assignment">
          <p>
            You may not assign your rights or obligations under these Terms without our prior
            written consent. MomentSent may assign these Terms in connection with a merger,
            acquisition, or sale of assets.
          </p>
        </SubSection>

        <SubSection title="15.6 Contact">
          <p>
            MomentSent LLC<br />
            P.O. Box 342<br />
            Hubert, NC 28539<br />
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>
          </p>
        </SubSection>
      </Section>

      <Section title="16. SMS Messaging Program (Text Reminders)">
        <p>
          MomentSent offers an SMS text-message program ("MomentSent Card Reminders")
          that sends account holders reminders about the card mailings they have
          scheduled, occasional service notifications, and a seasonal holiday-card
          reminder. Participation is optional and is not a condition of purchasing
          any goods or services.
        </p>

        <SubSection title="16.1 Consent and enrollment">
          <p>
            You opt in to the SMS program when you provide your mobile phone number and
            check the SMS consent box during account signup, which states that you agree
            to receive SMS reminders about your scheduled card mailings from MomentSent.
            You may also opt in by texting <strong>START</strong> to our messaging number.
            By opting in, you consent to receive recurring automated text messages at the
            number provided. Consent is not a condition of purchase.
          </p>
        </SubSection>

        <SubSection title="16.2 Message frequency and rates">
          <p>
            Message frequency varies based on the number of occasions and contacts in your
            account. <strong>Message and data rates may apply.</strong> Your mobile carrier's
            standard messaging, data, and other charges will apply to each message sent or
            received, in accordance with your wireless plan. MomentSent does not impose a
            separate charge for SMS messages.
          </p>
        </SubSection>

        <SubSection title="16.3 Opt-out and help">
          <p>
            You can cancel the SMS program at any time by texting <strong>STOP</strong> to
            our messaging number. After you send <strong>STOP</strong>, we will send a
            one-time confirmation message and you will no longer receive SMS messages from
            us. You may rejoin at any time by texting <strong>START</strong>. You may also
            opt out of additional message types such as <strong>STOPALL</strong>,{' '}
            <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>END</strong>, or{' '}
            <strong>QUIT</strong>.
          </p>
          <p>
            For help with the SMS program, text <strong>HELP</strong> to our messaging
            number or email{' '}
            <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
              hello@momentsent.com
            </a>
            .
          </p>
        </SubSection>

        <SubSection title="16.4 Supported carriers and delivery">
          <p>
            The SMS program is available on most major U.S. wireless carriers. Carriers are
            not liable for delayed or undelivered messages. Message delivery is subject to
            the effective transmission from your wireless carrier and is outside MomentSent's
            control. Carrier support may change without notice.
          </p>
        </SubSection>

        <SubSection title="16.5 Privacy">
          <p>
            Mobile information collected for the SMS program is handled in accordance with our{' '}
            <Link to="/privacy" className="text-sage hover:underline">Privacy Policy</Link>.
            We do not sell or share mobile opt-in data or phone numbers with third parties or
            affiliates for their marketing purposes. Phone numbers are shared only with our
            SMS provider (Twilio) solely to deliver the messages you have requested.
          </p>
        </SubSection>
      </Section>

    </LegalShell>
  );
}

// ── Layout components (same as PrivacyPage — kept local to avoid cross-import) ─

function LegalShell({
  title,
  effectiveDate,
  children,
}: {
  title: string;
  effectiveDate: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-cream">
      <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg font-bold text-navy hover:opacity-80 transition-opacity">
            Moment<span className="text-sage">Sent</span>
          </Link>
          <Link to="/" className="font-body text-xs text-gray-400 hover:text-navy transition-colors">
            ← Home
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="font-heading text-4xl font-bold text-navy mb-2">{title}</h1>
        <p className="font-body text-sm text-gray-400 mb-10">
          Effective {effectiveDate} · MomentSent LLC · Hubert, NC
        </p>

        <div className="prose-legal space-y-10">
          {children}
        </div>

        <div className="mt-16 pt-8 border-t border-gray-100 flex flex-wrap gap-4 text-xs font-body text-gray-400">
          <Link to="/privacy" className="hover:text-navy transition-colors">Privacy Policy</Link>
          <Link to="/terms"   className="hover:text-navy transition-colors">Terms of Service</Link>
          <a href="mailto:hello@momentsent.com" className="hover:text-navy transition-colors">
            Contact Support
          </a>
          <span className="ml-auto">© {new Date().getFullYear()} MomentSent LLC</span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-heading text-xl font-bold text-navy mb-4 pb-2 border-b border-gray-100">
        {title}
      </h2>
      <div className="font-body text-sm text-gray-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="font-body text-sm font-semibold text-navy mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
